// The Lessons list and the lesson page (047 step 6, D90–D103), as pure helpers: the short id,
// the head's tiles, the status line, the timeline, the wallet moves, the tutor's pay, the
// ratings and the list's filters. No React, MUI or Firebase, so Jest loads it directly. Every
// label is drawn from stored data; `timeZone` is the reader's unless a test pins one.
import { toDate, truncate } from "./ticketPresentation";
import { dayLabel, egp, money, timeLabel, whenLabel } from "./queuePresentation";
import { cancelOutcome, childKey, endOf, historyLines, partyOf, statusWord } from "./lessonPresentation";
import { sessionStatusChip } from "./studentDetail";
import { bookingPaidHours, rateAt } from "./teachingHours";
import { cyclePeriod, periodRange } from "./payCycle";
import { countText, showingLabel } from "./kitPresentation";

// "#a1B2c3D4": there are no lesson numbers (D96), so the id's first 8 characters, as a ticket
// without a number shows (047 D57).
export const lessonShortId = (id) => (id ? `#${String(id).slice(0, 8)}` : "—");

export const subjectText = (subject) => {
  const s = String(subject || "").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "";
};

// "Quran lesson" (D99).
export const lessonTitle = (b) => (subjectText(b?.subject) ? `${subjectText(b.subject)} lesson` : "Lesson");

// "Africa/Cairo" → "Cairo".
export const zoneCity = (zone) => (zone ? String(zone).split("/").pop().replace(/_/g, " ") : null);

// The credit tile (D99): what the lesson cost (createBooking's costFor: half a credit at 25
// minutes, else one) and where it went, from the flags a cancel writes (cancelOutcome).
export const creditTile = (b = {}) => {
  const value = b.isFreeSession ? "Free trial" : Number(b.duration) === 25 ? "½ credit" : "1 credit";
  if (b.status === "Pending") return { value, caption: "Held" };
  if (b.status !== "Cancelled") return { value, caption: "Spent" };
  const outcome = cancelOutcome(b);
  const caption =
    outcome === "Refunded" || outcome === "Free session returned"
      ? "Returned"
      : outcome === "Donated"
        ? "Donated"
        : outcome === "Not recorded"
          ? "Not recorded"
          : "Spent";
  return { value, caption };
};

// The time tile (D99): the day, then start – end in the reader's zone, named.
export const timeTile = (b, { timeZone } = {}) => {
  const start = toDate(b?.startTimeUTC);
  if (!start) return { value: "—", caption: "No time recorded" };
  const end = endOf(b);
  const range = end ? `${timeLabel(start, { timeZone })} – ${timeLabel(end, { timeZone })}` : timeLabel(start, { timeZone });
  const city = zoneCity(timeZone);
  return { value: dayLabel(start, { timeZone }), caption: city ? `${range} · ${city} time` : range };
};

// The length tile (D99): the minutes booked — the minutes taught are not stored.
export const lengthTile = (b) => ({ value: b?.duration ? `${Number(b.duration)} min` : "—", caption: "Booked" });

// The Participants tab's status line (D100): the chip, and the history line that matters most —
// a report, else a cancel, else the completion; for a Pending lesson, where it stands in time.
export const statusLine = (b, { nameOf, timeZone, now = new Date() } = {}) => {
  const chip = sessionStatusChip(b, now);
  const ticketTo = b?.ticketId ? `/inbox/${b.ticketId}` : null;
  const lines = historyLines(b, { nameOf, timeZone });
  const main = ["reported", "cancelled", "finished"].map((key) => lines.find((l) => l.key === key)).find(Boolean);
  if (main) return { chip, title: main.title, detail: [main.detail, main.chip].filter(Boolean).join(" · ") || null, ticketTo };
  const end = endOf(b);
  if (chip.label === "Upcoming") return { chip, title: `Starts ${whenLabel(b.startTimeUTC, { timeZone })}`, detail: null, ticketTo };
  if (chip.label === "Live now") return { chip, title: `In progress · ends ${timeLabel(end, { timeZone })}`, detail: null, ticketTo };
  if (chip.label === "Awaiting outcome") {
    return { chip, title: end ? `Ended ${whenLabel(end, { timeZone })}` : "Started", detail: "Nobody has marked it completed yet.", ticketTo };
  }
  return { chip, title: chip.label, detail: null, ticketTo };
};

const credits = (n) => (Number(n) === 0.5 ? "½ credit" : Number(n) === 1 ? "1 credit" : `${money(n)} credits`);

// One walletTransactions row about this lesson (D101, D102): what it did, and the balance it
// moved when the row carries both sides.
export const walletMove = (row, b, nameOf = () => undefined) => {
  const name = (uid) => nameOf(uid) || uid || "Someone";
  const kind = row.transactionType;
  let title;
  if (kind === "lesson") title = `${credits(row.amount)} charged from ${name(row.userId)}'s wallet`;
  else if (kind === "trial") title = "Free trial used";
  else if (kind === "trialReturned") title = "Free trial returned";
  else if (kind === "cancelledwithrefund") title = `${credits(row.amount)} returned to ${name(row.userId)}'s wallet`;
  else if (kind === "cancelledwithoutrefund") {
    title =
      row.type === "payout" || row.userId === b?.teacherId
        ? `${name(row.userId)} was paid for the late cancel`
        : b?.isFreeSession
          ? "Free trial not returned"
          : "No refund: the credit was kept";
  } else title = `Wallet change (${kind || row.type || "unknown"})`;
  const moved = typeof row.previousBalance === "number" && typeof row.newBalance === "number";
  return { title, sub: moved ? `Balance ${money(row.previousBalance)} → ${money(row.newBalance)}` : null };
};

const quote = (text) => (text ? `“${truncate(text, 120)}”` : null);

// The Activity tab (D101): every stored event that has a time, oldest first, each with its day
// and time. `learner` names who presses Join on the learner's side ("Lina").
export const activityEvents = (
  b,
  { walletRows = [], tickets = [], ratings = [], nameOf = () => undefined, learner, timeZone } = {}
) => {
  if (!b) return [];
  const name = (uid) => nameOf(uid) || uid || "Someone";
  const by = (uid) => `${name(uid)} (${partyOf(b, uid)})`;
  const events = [];
  const add = (key, at, title, sub, icon, tone = "neutral") => {
    const date = toDate(at);
    if (date) events.push({ key, at: date, title, sub: sub || null, icon, tone });
  };
  add("booked", b.createdAt, `${name(b.studentId)} booked the lesson`, `For ${whenLabel(b.startTimeUTC, { timeZone })}`, "booked", "brand");
  walletRows.forEach((row) => {
    const move = walletMove(row, b, nameOf);
    add(`wallet-${row.id}`, row.createdAt, move.title, move.sub, "wallet");
  });
  add("learner-joined", b.studentJoinedAt, `${learner || "The learner"} pressed Join`, null, "joined", "success");
  add("tutor-joined", b.teacherJoinedAt, `${name(b.teacherId)} pressed Join`, null, "joined", "success");
  if (b.finishedBy) add("finished", b.finishedAt, `Marked completed by ${by(b.finishedBy)}`, null, "completed", "success");
  if (b.status === "Cancelled" || b.cancelledBy) {
    add("cancelled", b.cancelledAt, b.cancelledBy ? `Cancelled by ${by(b.cancelledBy)}` : "Cancelled", b.cancelReason, "cancelled", "error");
  }
  if (b.reportedBy || b.reportReason) {
    add("reported", b.reportedAt, b.reportedBy ? `Reported by ${by(b.reportedBy)}` : "Reported", b.reportReason, "reported", "warning");
  }
  tickets.forEach((t) => add(`ticket-${t.id}`, t.raisedAt || t.createdAt, `Ticket ${t.number ? `#${t.number}` : String(t.id).slice(0, 8)} opened`, null, "ticket", "warning"));
  if (b.ticketId && !tickets.some((t) => t.id === b.ticketId)) add("ticket-raised", b.ticketRaisedAt, "A ticket was raised", null, "ticket", "warning");
  ratings.forEach((r) => {
    const what = r.role === "teacher" ? "the learner" : "the lesson";
    add(`rating-${r.id}`, r.createdAt, `${name(r.ratedBy)} rated ${what} ${r.rating}`, quote(r.comment), "rated", "brand");
  });
  if (b.parentNote) {
    add("note", b.parentNote.createdAt, `${name(b.teacherId)} wrote a note to the parent`, quote(b.parentNote.text), "note", "brand");
    add("note-seen", b.parentNote.seenAt, "The family read the note", null, "note");
  }
  return events
    .sort((x, y) => x.at - y.at)
    .map((e) => ({ ...e, day: dayLabel(e.at, { timeZone }), time: timeLabel(e.at, { timeZone }) }));
};

// The tutor's pay for this lesson (D102): the hours it pays (D85, 053 D1) × the tutor's rate at
// its start (salaryHistory), and the pay cycle its end falls in. Null when it pays nothing.
export const tutorPay = (b, tutor, versions) => {
  const hours = bookingPaidHours(b);
  if (!hours) return null;
  const end = endOf(b) || toDate(b.startTimeUTC);
  const cycle = end ? periodRange(cyclePeriod(versions, end)) : null;
  if (!tutor) return { hours, rate: null, amount: null, text: `${hours} h`, cycle };
  const rate = rateAt(tutor, b.startTimeUTC);
  const amount = hours * rate;
  return { hours, rate, amount, text: `${hours} h × ${egp(rate)} = ${egp(amount)}`, cycle };
};

const WORDS = { 5: "Excellent", 4: "Good", 3: "Okay", 2: "Poor", 1: "Very poor" };
export const ratingWord = (n) => WORDS[Math.round(Number(n))] || "";

// A lesson's two ratings (rateLesson's `role` is the rater's side): the learner's of the tutor,
// and the tutor's of the learner.
export const lessonRatings = (ratings = []) => ({
  learner: ratings.find((r) => r.role === "student") || null,
  tutor: ratings.find((r) => r.role === "teacher") || null,
});

// The tutor's latest ratings from other lessons (D103); `all` is newest first (getTutorRatings).
export const otherRatings = (all = [], lessonId, n = 3) => all.filter((r) => r.lessonId !== lessonId).slice(0, n);

// "2026-09-12" as the reader's local day, from its first to its last millisecond.
const dayBound = (text, end) => {
  const [y, m, d] = String(text).split("-").map(Number);
  if (!y || !m || !d) return null;
  return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
};

// The list's search and menus (D95), over the loaded lessons: the search looks where today's did
// (the id, the status and its word, the ids, the subject, the length, the names and phones, the
// child); Subject, Duration, Tutor and Learner match exactly; the date range is on the start.
export const filterLessons = (rows, filters = {}, { studentById = {}, teacherById = {}, childNames = {} } = {}) => {
  const q = String(filters.search || "").trim().toLowerCase();
  const from = filters.from ? dayBound(filters.from, false) : null;
  const to = filters.to ? dayBound(filters.to, true) : null;
  return rows.filter((b) => {
    if (filters.subject && b.subject !== filters.subject) return false;
    if (filters.duration && Number(b.duration) !== Number(filters.duration)) return false;
    if (filters.teacherId && b.teacherId !== filters.teacherId) return false;
    if (filters.studentId && b.studentId !== filters.studentId) return false;
    if (from || to) {
      const start = toDate(b.startTimeUTC);
      if (!start || (from && start < from) || (to && start > to)) return false;
    }
    if (!q) return true;
    const hay = [
      b.id,
      b.status,
      statusWord(b.status),
      b.studentId,
      b.teacherId,
      b.subject,
      b.duration != null ? String(b.duration) : null,
      studentById[b.studentId]?.name,
      studentById[b.studentId]?.phone,
      childNames[childKey(b)],
      teacherById[b.teacherId]?.name,
      teacherById[b.teacherId]?.phone,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
};

// The Date menu's choice (D95): "Any", "12 Sep 2026 – 20 Sep 2026", "From …" or "Until …".
export const dateRangeLabel = (from, to) => {
  const day = (text) => dayLabel(dayBound(text, false));
  if (from && to) return `${day(from)} – ${day(to)}`;
  if (from) return `From ${day(from)}`;
  return to ? `Until ${day(to)}` : "Any";
};

// The footer (D97): "Showing 1–25 of 500 loaded"; with a search or a menu on, how many of the
// loaded lessons match.
export const lessonsShowingLabel = (page, count, pageSize, matching, loaded) => {
  const range = showingLabel(page, count, pageSize);
  if (!range) return "";
  return matching === loaded
    ? `${range} of ${countText(loaded)} loaded`
    : `${range} of ${countText(matching)} matching · ${countText(loaded)} loaded`;
};

// The list head's three counts (D94); a count that failed or is still read is a dash.
export const countTiles = ({ total = null, completed = null, cancelled = null } = {}) =>
  [
    ["total", total, "TOTAL LESSONS"],
    ["completed", completed, "COMPLETED LESSONS"],
    ["cancelled", cancelled, "CANCELLED LESSONS"],
  ].map(([key, n, label]) => ({ key, value: typeof n === "number" ? countText(n) : "—", label }));

const ALL = { key: "all", label: "All" };

// The Subject menu: every subject among the loaded lessons, and the chosen one (`keep`) while it
// filters, so the button never reads "All" over a filter that is still on.
export const subjectOptions = (rows, keep) => [
  ALL,
  ...[...new Set([...rows.map((b) => b.subject), keep === ALL.key ? null : keep].filter(Boolean))]
    .map((s) => ({ key: s, label: subjectText(s) }))
    .sort((x, y) => x.label.localeCompare(y.label)),
];

// The Tutor and Learner menus: every tutor or student, by name.
export const personOptions = (byId = {}) => [
  ALL,
  ...Object.entries(byId)
    .map(([id, u]) => ({ key: id, label: (u && u.name) || id }))
    .sort((x, y) => x.label.localeCompare(y.label)),
];
